// --- inside LeaveRequestFormModal.js ---

import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  MenuItem,
  Typography,
} from "@mui/material";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import LeaveViewController from "../../redux/actions/leaveApiAction";
import { FadeLoader } from "react-spinners";
import { IMAGE_URL } from "../../services/api";
import { getUserInfoFromToken } from "../../utils/authUtils";
import ProtectedImage from "../../components/ProtectedImage";

function AutoCalculateLeaveDays() {
  const { values, setFieldValue } = useFormikContext();
  useEffect(() => {
    if (
      values.leave_start_date &&
      values.leave_end_date &&
      values.start_session &&
      values.end_session
    ) {
      const start = new Date(values.leave_start_date);
      const end = new Date(values.leave_end_date);

      if (start > end) {
        setFieldValue("total_leave_days", 0);
        return;
      }

      const dayDiff = Math.floor((end - start) / (1000 * 60 * 60 * 24));
      let total = 0;

      if (dayDiff === 0) {
        total = values.start_session === values.end_session ? 0.5 : 1;
      } else {
        total = dayDiff + 1;
        if (values.start_session === "2") total -= 0.5;
        if (values.end_session === "1") total -= 0.5;
      }

      setFieldValue("total_leave_days", total);
    }
  }, [
    values.leave_start_date,
    values.leave_end_date,
    values.start_session,
    values.end_session,
    setFieldValue,
  ]);

  return null;
}

function LeaveRequestFormModal({ show, handleClose, initialData = null }) {
  const [userType, setUserType] = useState(null);
  const [userId, setUserId] = useState(null);
  const dispatch = useDispatch();
  const { addLeaves, updateLeaveStatus } = LeaveViewController();
  const isEdit = Boolean(initialData?.id);
  const { isLoding } = useSelector((state) => state.leave);

  const [initialValues, setInitialValues] = useState({
    employee: "",
    leave_type: "paid",
    leave_start_date: "",
    leave_end_date: "",
    total_leave_days: "",
    reason: "",
    approval_status: "pending",
    attachments: [],
    start_session: "1",
    end_session: "2",
  });

  const [existingAttachments, setExistingAttachments] = useState([]);

  useEffect(() => {
    if (show && initialData) {
      setInitialValues({
        employee: initialData.employee.id || "",
        leave_type: initialData.leave_type || "",
        leave_start_date: initialData.leave_start_date || "",
        leave_end_date: initialData.leave_end_date || "",
        total_leave_days: initialData.total_leave_days || "",
        reason: initialData.reason || "",
        approval_status: initialData.approval_status || "pending",
        attachments: [],
        start_session: initialData.start_session || "1",
        end_session: initialData.end_session || "2",
      });
      setExistingAttachments(initialData.attachments || []);
    } else if (show && !initialData) {
      setInitialValues({
        employee: userType === "Employee" ? userId : "",
        leave_type: "paid",
        leave_start_date: "",
        leave_end_date: "",
        total_leave_days: "",
        reason: "",
        approval_status: "pending",
        attachments: [],
        start_session: "1",
        end_session: "2",
      });
      setExistingAttachments([]);
    }
  }, [show, initialData, userType, userId]);

  useEffect(() => {
    const { userType, userId } = getUserInfoFromToken();
    setUserType(userType);
    setUserId(userId);
  }, []);

  const handleSubmit = (values, { resetForm }) => {
    const formData = new FormData();
    for (const key in values) {
      if (key !== "attachments") {
        formData.append(key, values[key]);
      }
    }
    if (values.attachments && values.attachments.length > 0) {
      Array.from(values.attachments).forEach((file) => {
        formData.append("attachments", file);
      });
    }

    const action = isEdit
      ? updateLeaveStatus(initialData.id, formData)
      : addLeaves(formData);

    dispatch(action)
      .then(() => {
        resetForm();
        handleClose();
      })
      .catch((error) => {
        console.error("Error submitting form:", error);
      });
  };

  const validationSchema = Yup.object({
    leave_type: Yup.string().required("Leave type is required"),
    leave_start_date: Yup.date().required("Start date is required"),
    leave_end_date: Yup.date().required("End date is required"),
    reason: Yup.string()
      .required("Reason is required")
      .min(100, "Reason must be 100 characters"),
  });

  return (
    <Dialog open={show} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>{isEdit ? "Edit Leave Request" : "Request Leave"}</DialogTitle>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue, values, errors, touched, handleChange }) => (
          <Form>
            <DialogContent
              dividers
              style={{ maxHeight: "65vh", overflowY: "auto" }}
            >
              <AutoCalculateLeaveDays />

              {/* Leave Type */}
              <Typography gutterBottom>
                Leave Type <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                select
                name="leave_type"
                value={values.leave_type}
                onChange={handleChange}
                fullWidth
                error={touched.leave_type && Boolean(errors.leave_type)}
                helperText={touched.leave_type && errors.leave_type}
                size="small"
              >
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="unpaid">Unpaid</MenuItem>
                <MenuItem value="sick">Sick</MenuItem>
              </TextField>

              {/* Start Date + Start Session */}
              <Typography gutterBottom className="mt-3">
                Leave Start Date <span style={{ color: "red" }}>*</span>
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <TextField
                    type="date"
                    name="leave_start_date"
                    value={values.leave_start_date}
                    onChange={handleChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={touched.leave_start_date && Boolean(errors.leave_start_date)}
                    helperText={touched.leave_start_date && errors.leave_start_date}
                    size="small"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    select
                    name="start_session"
                    value={values.start_session}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="1">Session 1</MenuItem>
                    <MenuItem value="2">Session 2</MenuItem>
                  </TextField>
                </Grid>
              </Grid>

              {/* End Date + End Session */}
              <Typography gutterBottom className="mt-3">
                Leave End Date <span style={{ color: "red" }}>*</span>
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <TextField
                    type="date"
                    name="leave_end_date"
                    value={values.leave_end_date}
                    onChange={handleChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={touched.leave_end_date && Boolean(errors.leave_end_date)}
                    helperText={touched.leave_end_date && errors.leave_end_date}
                    size="small"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    select
                    name="end_session"
                    value={values.end_session}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="1">Session 1</MenuItem>
                    <MenuItem value="2">Session 2</MenuItem>
                  </TextField>
                </Grid>
              </Grid>

              {/* Total Leave Days */}
              <Typography gutterBottom className="mt-3">
                Total Leave Days
              </Typography>
              <TextField
                name="total_leave_days"
                value={values.total_leave_days}
                fullWidth
                InputProps={{ readOnly: true }}
                size="small"
              />

              {/* Reason */}
              <Typography gutterBottom className="mt-3">
                Reason <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                name="reason"
                multiline
                rows={3}
                value={values.reason}
                onChange={handleChange}
                fullWidth
                error={touched.reason && Boolean(errors.reason)}
                helperText={touched.reason && errors.reason}
                size="small"
              />

              {/* Upload Attachments */}
              <Typography gutterBottom className="mt-3">
                Attachments
              </Typography>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                size="small"
              >
                Upload Files
                <input
                  name="attachments"
                  type="file"
                  multiple
                  hidden
                  onChange={(event) => {
                    setFieldValue("attachments", event.currentTarget.files);
                  }}
                />
              </Button>

              {values.attachments && values.attachments.length > 0 && (
                <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                  {Array.from(values.attachments).map((file, index) => (
                    <li key={index} style={{ fontSize: "0.875rem" }}>
                      {file.name}
                    </li>
                  ))}
                </ul>
              )}
            </DialogContent>

            <DialogActions>
              <Button variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" variant="contained">
                {isEdit ? "Update" : "Submit"}
              </Button>

              {isLoding && (
                <div
                  style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    height: "100vh",
                    width: "100vw",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    background: "rgba(255, 255, 255, 0.6)",
                    zIndex: 9999,
                  }}
                >
                  <FadeLoader loading={true} size={150} />
                </div>
              )}
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}

export default LeaveRequestFormModal;
